import { API_BASE } from "../config";
import React, { useState, useEffect, useRef } from 'react';
import DietComplianceChecker from './DietComplianceChecker';
import { lookupProductByUPC } from '../utils/barcode-helpers';
import { useBarcodeScanner } from '../hooks/useBarcodeScanner';

import { useEscapeToClose } from '../hooks/useEscapeToClose';
import UhcCompatBadge from './UhcCompatBadge';
// Category mapping from Edamam to form dropdown
const mapEdamamCategory = (edamamCategory) => {
  if (!edamamCategory) return '';
  const cat = edamamCategory.toLowerCase();
  
  const mapping = {
    'packaged foods': 'Pantry',
    'canned foods': 'Pantry',
    'canned seafood': 'Seafood',
    'dairy': 'Dairy',
    'cheese': 'Dairy',
    'milk': 'Dairy',
    'yogurt': 'Dairy',
    'produce': 'Produce',
    'fruits': 'Produce',
    'vegetables': 'Produce',
    'meat': 'Meat',
    'poultry': 'Meat',
    'seafood': 'Seafood',
    'fish': 'Seafood',
    'bread': 'Bakery',
    'bakery': 'Bakery',
    'frozen': 'Frozen',
    'beverages': 'Beverages',
    'drinks': 'Beverages',
    'snacks': 'Snacks',
    'condiments': 'Condiments',
    'sauces': 'Condiments'
  };
  
  for (const [key, value] of Object.entries(mapping)) {
    if (cat.includes(key)) return value;
  }
  return 'Other';
};

// Pantry Item Creation/Edit Form with Built-in Barcode Scanner
export function PantryItemForm({ onClose, onSave, fetchWithAuth, editingItem, pantryId, initialBarcode, title }) {
  useEscapeToClose(onClose);   // Escape dismisses this modal
  // Self-resolve pantryId if not provided (one user = one pantry)
  const [resolvedPantryId, setResolvedPantryId] = useState(pantryId || null);

  useEffect(() => {
    if (!pantryId && fetchWithAuth) {
      fetchWithAuth(`${API_BASE}/api/pantry/`)
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data?.id) setResolvedPantryId(data.id); })
        .catch(() => {});
    }
  }, [pantryId, fetchWithAuth]);

  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    barcode: initialBarcode || '',
    category: '',
    quantity: 1,
    package_size: '',
    package_unit: 'pieces',
    unit: 'pieces',
    purchase_date: new Date().toISOString().split('T')[0],  // Default to today
    expiration_date: '',
    location_in_pantry: '',
    notes: '',
    minimum_quantity: 1,
    out_of_stock: false,
    storage_method: '',
    foodkeeper_product_id: null,
    expiry_source: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Full-UHC verdict returned by the save; when set, the post-save notice replaces the form.
  const [uhcVerdict, setUhcVerdict] = useState(null);
  const [forceStockout, setForceStockout] = useState(false);
  const [lookingUpBarcode, setLookingUpBarcode] = useState(false);
  const [shelfLifeInfo, setShelfLifeInfo] = useState(null);  // FoodKeeper data
  const [shelfLifeLoading, setShelfLifeLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showCameraScanner, setShowCameraScanner] = useState(false);
  const [isCameraScanning, setIsCameraScanning] = useState(false);
  const scannerHostRef = useRef(null);
  const scannedCodesRef = useRef(new Set());
  const [scannedProductData, setScannedProductData] = useState(null);
  const [complianceResult, setComplianceResult] = useState(null);
  const [hasLookedUp, setHasLookedUp] = useState(false);

  // STRICH SDK hook for mobile camera scanning
  const {
    sdkReady: cameraSdkReady,
    error: cameraError,
    startCameraScanning,
    stopCameraScanning
  } = useBarcodeScanner({ fetchWithAuth });

  // Cleanup camera on unmount
  useEffect(() => {
    return () => { stopCameraScanning(); };
  }, [stopCameraScanning]);

  // Start camera when scanner section is shown
  const [cameraStartError, setCameraStartError] = useState('');
  useEffect(() => {
    if (!showCameraScanner) return;

    setCameraStartError('');
    // Wait for DOM to fully paint before starting camera (matches VGC pattern)
    const timer = setTimeout(async () => {
      const hostEl = document.querySelector('#pf-scanner-host');
      if (!hostEl) {
        setCameraStartError('Scanner element not found. Please try again.');
        return;
      }
      try {
        const success = await startCameraScanning({
          selector: '#pf-scanner-host',
          onDetection: async (detections) => {
            for (const detection of detections) {
              const barcode = detection.data;
              if (scannedCodesRef.current.has(barcode)) continue;
              scannedCodesRef.current.add(barcode);
              // Stop camera, populate barcode field, auto-lookup
              await stopCameraScanning();
              setIsCameraScanning(false);
              setShowCameraScanner(false);
              setFormData(prev => ({ ...prev, barcode }));
              lookupBarcodeInfo(barcode);
            }
          }
        });
        if (success) {
          setIsCameraScanning(true);
        } else {
          setCameraStartError('Camera could not start. Check camera permissions or use manual entry.');
        }
      } catch (err) {
        setCameraStartError(`Camera error: ${err.message}`);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [showCameraScanner]);

  const handleStopCamera = async () => {
    await stopCameraScanning();
    setIsCameraScanning(false);
    setShowCameraScanner(false);
    scannedCodesRef.current.clear();
  };

  // Handle product change from DietComplianceChecker "Check Another"
  const handleComplianceProductChange = (newProduct) => {
    if (newProduct) {
      const notesParts = [];
      if (newProduct.kcal_per_100g) notesParts.push(`${Math.round(newProduct.kcal_per_100g)} kcal/100g`);
      if (newProduct.protein_per_100g) notesParts.push(`${newProduct.protein_per_100g}g protein`);
      if (newProduct.serving_description) notesParts.push(`Serving: ${newProduct.serving_description}`);
      if (newProduct.nutri_score) notesParts.push(`Nutri-Score: ${newProduct.nutri_score.toUpperCase()}`);
      
      setFormData(prev => ({
        ...prev,
        name: newProduct.label || prev.name,
        brand: newProduct.brand || prev.brand,
        category: mapEdamamCategory(newProduct.category) || prev.category,
        barcode: newProduct.upc || prev.barcode,
        notes: notesParts.length > 0 ? notesParts.join(' | ') : prev.notes
      }));
      // Trigger FoodKeeper shelf life lookup
      if (newProduct.label) {
        lookupShelfLife(newProduct.label, formData.storage_method);
      }
      setScannedProductData(newProduct);
    }
  };

  // FoodKeeper shelf life lookup when name changes
  const lookupShelfLife = async (foodName, storageMethod) => {
    if (!foodName || foodName.trim().length < 2) {
      setShelfLifeInfo(null);
      return;
    }
    setShelfLifeLoading(true);
    try {
      const params = new URLSearchParams({
        food_name: foodName.trim(),
        storage_method: storageMethod || formData.storage_method || 'refrigerator'
      });
      const response = await fetchWithAuth(`${API_BASE}/api/pantry/estimate-expiry?${params}`);
      if (response.ok) {
        const data = await response.json();
        setShelfLifeInfo(data);
        // Auto-fill expiry date and save FoodKeeper product linkage
        const updates = {};
        if (!formData.expiration_date && data.expiry_date) {
          updates.expiration_date = data.expiry_date;
        }
        if (data.foodkeeper_product_id) {
          updates.foodkeeper_product_id = data.foodkeeper_product_id;
          updates.expiry_source = 'foodkeeper';
        } else {
          updates.foodkeeper_product_id = null;
          updates.expiry_source = data.source || 'estimated';
        }
        if (Object.keys(updates).length > 0) {
          setFormData(prev => ({ ...prev, ...updates }));
        }
      }
    } catch (err) {
      console.error('Shelf life lookup failed:', err);
      setShelfLifeInfo(null);
    } finally {
      setShelfLifeLoading(false);
    }
  };

  // Re-lookup when storage method changes (different shelf life per method)
  const handleStorageMethodChange = (newMethod) => {
    setFormData(prev => ({ ...prev, storage_method: newMethod }));
    if (formData.name && formData.name.trim().length >= 2) {
      lookupShelfLife(formData.name, newMethod);
    }
  };

  useEffect(() => {
    // Detect if device is mobile with more robust detection
    const checkMobile = () => {
      // Check user agent
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
      const isMobileUA = mobileRegex.test(userAgent);
      
      // Check screen size
      const isSmallScreen = window.innerWidth <= 768;
      
      // Check for touch capability
      const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      
      // Device is mobile if it matches UA pattern OR (small screen AND touch)
      return isMobileUA || (isSmallScreen && hasTouch);
    };
    
    setIsMobile(checkMobile());

    if (editingItem) {
      setFormData({
        name: editingItem.name || '',
        brand: editingItem.brand || '',
        barcode: editingItem.barcode || '',
        category: editingItem.category || '',
        quantity: editingItem.quantity || 1,
        package_size: editingItem.package_size || '',
        package_unit: editingItem.package_unit || 'pieces',
        unit: editingItem.unit || 'pieces',
        purchase_date: editingItem.purchase_date || '',
        expiration_date: editingItem.expiration_date || '',
        location_in_pantry: editingItem.location_in_pantry || '',
        notes: editingItem.notes || '',
        minimum_quantity: editingItem.minimum_quantity || 1,
        out_of_stock: editingItem.out_of_stock || false,
        storage_method: editingItem.storage_method || ''
      });
      setForceStockout(editingItem.out_of_stock || false);
    } else if (initialBarcode && !hasLookedUp) {
      // Auto-lookup barcode when provided, but only once
      lookupBarcodeInfo(initialBarcode);
      setHasLookedUp(true);
    }
  }, [editingItem, initialBarcode, hasLookedUp]);

  const lookupBarcodeInfo = async (barcode) => {
    if (!barcode || !barcode.trim()) return;

    setHasLookedUp(true);
    setLookingUpBarcode(true);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const result = await lookupProductByUPC(fetchWithAuth, barcode, { signal: controller.signal });
      clearTimeout(timeoutId);

      if (result.found && result.product) {
        const product = result.product;

        // Build notes from product info
        const notesParts = [];
        if (product.kcal_per_100g) notesParts.push(`${Math.round(product.kcal_per_100g)} kcal/100g`);
        if (product.protein_per_100g) notesParts.push(`${product.protein_per_100g}g protein`);
        if (product.serving_description) notesParts.push(`Serving: ${product.serving_description}`);
        if (product.nutri_score) notesParts.push(`Nutri-Score: ${product.nutri_score.toUpperCase()}`);

        // Parse serving size into package_size + package_unit
        let parsedSize = null;
        let parsedUnit = null;
        const unitMap = { 'oz': 'oz', 'g': 'g bags', 'ml': 'mL bottles', 'cup': 'cups', 'cups': 'cups', 'lb': 'lbs', 'lbs': 'lbs', 'fl oz': 'oz bottles' };
        if (product.serving_description) {
          const desc = product.serving_description;
          let m = desc.match(/^([\d.]+)\s*(oz|g|ml|cup|cups|lbs|lb|fl oz)/i);
          if (!m) m = desc.match(/\(([\d.]+)\s*(oz|g|ml|cup|cups|lbs|lb|fl oz)\)/i);
          if (!m) m = desc.match(/([\d.]+)\s*(oz|g|ml|cup|cups|lbs|lb|fl oz)/i);
          if (m) {
            parsedSize = parseFloat(m[1]);
            parsedUnit = unitMap[m[2].toLowerCase()] || 'pieces';
          }
        }
        if (!parsedSize && product.serving_size_g) {
          parsedSize = product.serving_size_g;
          parsedUnit = product.serving_size_g >= 100 ? 'g bags' : 'oz';
        }

        setFormData(prev => ({
          ...prev,
          name: product.label || prev.name,
          brand: product.brand || prev.brand,
          category: mapEdamamCategory(product.category) || prev.category,
          package_size: parsedSize || prev.package_size,
          package_unit: parsedUnit || prev.package_unit,
          unit: parsedUnit || prev.unit,
          notes: notesParts.length > 0 ? notesParts.join(' | ') : prev.notes
        }));
        if (product.label) {
          lookupShelfLife(product.label, formData.storage_method);
        }
        setScannedProductData({
          upc: barcode,
          label: product.label,
          brand: product.brand,
          category: product.category,
          image_url: product.image_url,
          kcal_per_100g: product.kcal_per_100g,
          protein_per_100g: product.protein_per_100g,
          carbs_per_100g: product.carbs_per_100g,
          fat_per_100g: product.fat_per_100g,
          nova_group: product.nova_group,
          nutri_score: product.nutri_score,
          eco_score: product.eco_score,
          allergens_tags: product.allergens_tags,
          health_labels: product.health_labels
        });
      } else {
        setScannedProductData(null);
        setComplianceResult(null);
        alert(result.message || result.error || 'Product not found in database. Please enter details manually.');
      }
    } catch (error) {
      console.error('Failed to lookup barcode:', error);
      alert('Barcode lookup failed. Please check your connection and try again.');
    } finally {
      setLookingUpBarcode(false);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const activePantryId = resolvedPantryId || pantryId;
      const url = editingItem
        ? `${API_BASE}/api/pantry/${activePantryId}/items/${editingItem.id}`
        : `${API_BASE}/api/pantry/${activePantryId}/items`;
    
      const method = editingItem ? 'PUT' : 'POST';
    
      // Clean up data - convert empty date strings to null
      const submitData = {
        name: formData.name,
        brand: formData.brand || null,
        barcode: formData.barcode || null,
        category: formData.category || null,
        quantity: parseFloat(formData.quantity),
        package_size: formData.package_size ? parseFloat(formData.package_size) : null,
        package_unit: formData.package_unit || null,
        unit: formData.package_unit || formData.unit,
        minimum_quantity: parseFloat(formData.minimum_quantity),
        purchase_date: formData.purchase_date === '' ? null : formData.purchase_date,
        expiration_date: formData.expiration_date === '' ? null : formData.expiration_date,
        location_in_pantry: formData.location_in_pantry || null,
        notes: formData.notes || null,
        out_of_stock: forceStockout,
        storage_method: formData.storage_method || null,
        foodkeeper_product_id: formData.foodkeeper_product_id || null,
        expiry_source: formData.expiry_source || null
      };
    
      const response = await fetchWithAuth(url, {
        method: method,
        body: JSON.stringify(submitData)
      });
    
      if (response.ok) {
        console.log('Item saved successfully');
        // Full-UHC compatibility verdict (#78).
        // WAS BROKEN: this block read `result.drug_interactions`, the key the narrow
        // drug-only check used BEFORE #78 consolidated it into the single-source UHC gate.
        // No endpoint has returned that key since, so the whole warning — including the
        // CRITICAL path — was dead code and the verdict was silently discarded.
        // The live key is `uhc_compatibility` {severity: avoid|caution|advisory, reasons[]}.
        let verdict = null;
        try {
          const result = await response.json();
          const uhc = result && result.uhc_compatibility;
          if (uhc && uhc.severity && uhc.severity !== 'clear') verdict = uhc;
        } catch (e) {
          console.warn('Pantry save: could not parse response for UHC verdict', e);
        }

        // Refresh the list first so the item (and its badge) is present behind the notice.
        if (onSave) {
          await onSave();
        }

        if (verdict) {
          // Hold the surface open and TELL the user, at the moment they acted. A badge in a
          // list they may never scroll to is not the same as being told.
          setUhcVerdict(verdict);
        } else {
          onClose();
        }
      } else {
        const errorText = await response.text();
        alert(`Failed to save item: ${errorText}`);
      }
    } catch (error) {
      console.error('Failed to save item:', error);
      alert('Failed to save item. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };  

  // Post-save Full-UHC notice. The item IS saved (the gate is advisory by design — the
  // backend never blocks item creation on it), so this reports rather than asks. It replaces
  // a dead window.confirm whose return value was discarded anyway, so "Cancel" and "OK"
  // did the same thing on what was supposed to be a critical safety warning.
  if (uhcVerdict) {
    const dismiss = () => { setUhcVerdict(null); onClose(); };
    return (
      <div className="modal-overlay">
        <div className="modal-content pantry-item-modal">
          <div className="modal-header">
            <h3>{uhcVerdict.severity === 'unavailable' ? 'Saved — not checked yet' : 'Saved — one thing to know'}</h3>
            <button className="modal-close" onClick={dismiss}>&times;</button>
          </div>
          <div className="pf-uhcVerdictBody">
            {uhcVerdict.severity === 'unavailable' ? (
              // The check could not run (2026-11-02) — say so; never claim it was checked.
              <p className="pf-uhcVerdictIntro">
                <strong>{formData.name}</strong> was added to your pantry, but Healist could not
                check it against your health context — medications, conditions, labs, allergens and
                diet — just now:
              </p>
            ) : (
              <p className="pf-uhcVerdictIntro">
                <strong>{formData.name}</strong> was added to your pantry. Healist checked it against
                your complete health context — medications, conditions, labs, allergens and diet — and
                flagged this:
              </p>
            )}
            <UhcCompatBadge compatibility={uhcVerdict} />
            <p className="pf-uhcVerdictFooter">
              This is informational and does not replace advice from your healthcare provider.
            </p>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-primary" onClick={dismiss}>Got It</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content pantry-item-modal">
        <div className="modal-header">
          <h3>{title || (editingItem ? 'Edit Pantry Item' : 'Add Pantry Item')}</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Barcode Section at Top - ONLY for Add mode, not Edit mode */}
          {!editingItem && (
          <div className="barcode-section">
            <div className="form-group">
              <label className="form-label">Barcode</label>
              <div className="pf-barcodeInputGroup">
                <input
                  type="text"
                  className="form-input"
                  value={formData.barcode}
                  onChange={(e) => setFormData(prev => ({...prev, barcode: e.target.value}))}
                  placeholder="Enter UPC/EAN barcode manually"
                />
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => lookupBarcodeInfo(formData.barcode)}
                  disabled={lookingUpBarcode || !formData.barcode}
                >
                  {lookingUpBarcode ? 'Looking up...' : 'Lookup'}
                </button>
              </div>
            </div>
            
            {/* Mobile-Only Scan Button + Inline Camera */}
            {isMobile && !showCameraScanner && (
              <div className="form-group">
                <button
                  type="button"
                  className="btn btn-primary scan-barcode-btn pf-scanBarcodeBtn"
                  onClick={() => { scannedCodesRef.current.clear(); setShowCameraScanner(true); }}
                  disabled={!cameraSdkReady}
                >
                  {cameraSdkReady ? 'Scan Item Barcode' : 'Loading Scanner...'}
                </button>
              </div>
            )}

            {/* Inline Camera Scanner — host always in DOM (hidden/shown via display, matches VGC pattern) */}
            <div className="form-group pf-inlineScanner" style={{ display: showCameraScanner ? 'block' : 'none' }}>
              <div
                id="pf-scanner-host"
                ref={scannerHostRef}
                className="pf-scannerHost"
              />
              {cameraStartError && (
                <p className="pf-cameraError">{cameraStartError}</p>
              )}
              {cameraError && !cameraStartError && (
                <p className="pf-cameraError">{cameraError}</p>
              )}
              <button
                type="button"
                className="btn btn-secondary pf-stopScanBtn"
                onClick={handleStopCamera}
              >
                Stop Scanner
              </button>
            </div>
          </div>
          )}

          {/* Diet Compliance Check */}
          {scannedProductData && (
            <div className="pf-complianceChecker">
              <DietComplianceChecker
              token={localStorage.getItem('token')}
              productData={scannedProductData}
              onComplianceResult={(result) => setComplianceResult(result)}
              onProductChange={handleComplianceProductChange}
              embedded={true}
              showProductInfo={false}
              />
            </div>
          )}

          {/* Item Details */}
          <div className="pf-fieldRow">
            <div className="form-group">
              <label className="form-label">Item Name *</label>
              <input
                type="text"
                className="form-input"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({...prev, name: e.target.value}))}
                onBlur={(e) => lookupShelfLife(e.target.value)}
                placeholder="e.g. Whole Milk, Bananas"
                required
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Brand</label>
              <input
                type="text"
                className="form-input"
                value={formData.brand}
                onChange={(e) => setFormData(prev => ({...prev, brand: e.target.value}))}
                placeholder="e.g. Kraft, Dole"
              />
            </div>
          </div>

          <div className="pf-fieldRow">
            <div className="form-group">
              <label className="form-label">Category</label>
              <select
                className="form-select"
                value={formData.category}
                onChange={(e) => setFormData(prev => ({...prev, category: e.target.value}))}
              >
                <option value="">Select category...</option>
                <option value="Dairy">Dairy</option>
                <option value="Produce">Produce</option>
                <option value="Meat">Meat & Poultry</option>
                <option value="Seafood">Seafood</option>
                <option value="Bakery">Bakery</option>
                <option value="Pantry">Pantry Staples</option>
                <option value="Frozen">Frozen</option>
                <option value="Beverages">Beverages</option>
                <option value="Snacks">Snacks</option>
                <option value="Condiments">Condiments</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Storage Method</label>
              <select
                className="form-select"
                value={formData.storage_method}
                onChange={(e) => handleStorageMethodChange(e.target.value)}
              >
                <option value="">Select...</option>
                <option value="refrigerator">Refrigerator</option>
                <option value="pantry">Pantry / Shelf</option>
                <option value="freezer">Freezer</option>
              </select>
            </div>
          </div>

          {/* Quantity Section - 3 Field System */}
          <div className="pf-quantitySection">
            <label className="form-label pf-quantityLabel">
              Quantity <span className="pf-requiredIndicator">*</span>
            </label>
            <div className="pf-quantityRow">
              <div className="pf-countField">
                <span className="pf-countLabel">Count</span>
                <input
                  type="number"
                  min="1"
                  step="1"
                  className="form-input pf-countInput"
                 
                  value={formData.quantity}
                  onChange={(e) => setFormData(prev => ({...prev, quantity: e.target.value}))}
                  required
                />
              </div>
              <span className="pf-separator">×</span>
              <div className="pf-sizeField">
                <span className="pf-sizeLabel">Size</span>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  className="form-input pf-sizeInput"
                 
                  value={formData.package_size}
                  onChange={(e) => setFormData(prev => ({...prev, package_size: e.target.value}))}
                  placeholder="opt"
                />
              </div>
              <div className="pf-unitField">
                <span className="pf-unitLabel">Unit</span>
                <select
                  className="form-select"
                  value={formData.package_unit}
                  onChange={(e) => setFormData(prev => ({...prev, package_unit: e.target.value, unit: e.target.value}))}
                >
                  <option value="pieces">pieces</option>
                  <option value="oz cans">oz cans</option>
                  <option value="oz jars">oz jars</option>
                  <option value="oz bottles">oz bottles</option>
                  <option value="oz bags">oz bags</option>
                  <option value="oz boxes">oz boxes</option>
                  <option value="lb bags">lb bags</option>
                  <option value="L bottles">L bottles</option>
                  <option value="mL bottles">mL bottles</option>
                  <option value="g bags">g bags</option>
                  <option value="kg bags">kg bags</option>
                  <option value="cups">cups</option>
                  <option value="lbs">lbs</option>
                  <option value="oz">oz</option>
                </select>
              </div>
            </div>
            {/* Quantity Preview */}
            {formData.quantity && (
              <div className="pf-quantityPreview">
                {formData.package_size ? (
                  <>You have: <strong className="pf-totalQuantity">{formData.quantity} × {formData.package_size}{formData.package_unit.replace(/^\w+\s/, '')}</strong> ({(parseFloat(formData.quantity || 0) * parseFloat(formData.package_size || 0)).toFixed(1)}{formData.package_unit.split(' ')[0]} total)</>
                ) : (
                  <>You have: <strong className="pf-simpleQuantity">{formData.quantity} {formData.package_unit}</strong></>
                )}
              </div>
            )}
          </div>

          <div className="form-group pf-alertSection">
            <label className="form-label">Low Stock Alert</label>
            <input
              type="number"
              step="1"
              min="0"
              className="form-input pf-alertThreshold"
             
              value={formData.minimum_quantity}
              onChange={(e) => setFormData(prev => ({...prev, minimum_quantity: e.target.value}))}
              placeholder="Alert when below this count"
            />
          </div>

          <div className="pf-fieldRow">
            <div className="form-group">
              <label className="form-label">{editingItem ? 'Scan Date' : 'Purchase Date'}</label>
              <input
                type="date"
                className="form-input"
                value={formData.purchase_date}
                onChange={(e) => setFormData(prev => ({...prev, purchase_date: e.target.value}))}
                readOnly={!!editingItem}
                style={editingItem ? { backgroundColor: '#F3F4F6', cursor: 'not-allowed' } : {}}
              />
              {editingItem && (
                <p className="pf-readOnlyWarning">
                  Scan date cannot be modified
                </p>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">
                Expiration / Use-By Date
                {shelfLifeLoading && <span className="pf-shelfLifeLoading"> (looking up...)</span>}
              </label>
              <input
                type="date"
                className="form-input"
                value={formData.expiration_date}
                onChange={(e) => setFormData(prev => ({...prev, expiration_date: e.target.value}))}
                placeholder="Auto-populated from USDA data when available"
              />
            </div>
          </div>

          {/* Shelf Life Notes — read-only */}
          {shelfLifeInfo && shelfLifeInfo.source === 'foodkeeper' && (
            <div className="form-group">
              <label className="form-label">USDA FoodKeeper Notes</label>
              <textarea
                className="form-textarea pf-foodkeeperNotes"
                readOnly
                rows="3"
                value={[
                  `Matched: ${shelfLifeInfo.product_name || 'Unknown product'}`,
                  `Shelf life: ${shelfLifeInfo.expiry_days} days${shelfLifeInfo.expiry_days_max && shelfLifeInfo.expiry_days_max !== shelfLifeInfo.expiry_days ? ` – ${shelfLifeInfo.expiry_days_max} days` : ''} (${formData.storage_method || 'default storage'})`,
                  shelfLifeInfo.tips ? `Tips: ${shelfLifeInfo.tips}` : null,
                  shelfLifeInfo.freeze_not_recommended ? '⚠ Freezing not recommended for this item' : null
                ].filter(Boolean).join('\n')}
              />
            </div>
          )}
          {shelfLifeInfo && shelfLifeInfo.source === 'fallback' && formData.name && (
            <div className="form-group">
              <label className="form-label">Shelf Life Estimate</label>
              <textarea
                className="form-textarea pf-foodkeeperNotes"
                readOnly
                rows="2"
                value={[
                  `Estimated: ${shelfLifeInfo.expiry_days} days (based on ${shelfLifeInfo.category || 'food'} category)`,
                  'No exact USDA FoodKeeper match — verify expiry date on packaging'
                ].join('\n')}
              />
            </div>
          )}

          <div className="form-group">
            <label className="form-label pf-forceStockoutLabel">
              <input
                type="checkbox"
                checked={forceStockout}
                onChange={(e) => setForceStockout(e.target.checked)}
                className="pf-forceStockoutCheckbox"
              />
              Force Stockout
            </label>
            <p className="pf-stockoutHelp">
              Check this to mark the item as out of stock when updating
            </p>
          </div>

          <div className="form-group">
            <label className="form-label">Additional Notes</label>
            <textarea
              className="form-textarea"
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({...prev, notes: e.target.value}))}
              placeholder="Any additional notes about this item"
              rows="3"
            />
          </div>
          
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : (editingItem ? 'Update Item' : 'Add Item')}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
}